import { Spacing } from '@persona-id/design-tokens';
import { LinkIcon } from '@persona-id/design-tokens/icons';
import produce from 'immer';
import { memo, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import StyleInput, {
  StyleInputContainer,
  StyleLabel,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/inputs/StyleInput';
import Button from 'design-system/components/Button';
import Tooltip from 'design-system/components/Tooltip';
import useToggle from 'lib/hooks/useToggle';
import { StyleValueBoxDimensionsConfig } from 'lib/inquiryStyles';

const LinkedInputsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const LinkedInputs = styled.div`
  display: flex;
  margin-right: ${Spacing[2]};

  ${StyleInputContainer} {
    input {
      &:hover:not(&:disabled) {
        border-width: 1px;
      }
      &:focus:not(&[data-has-error='true']),
      &:focus-within:not(&[data-has-error='true']) {
        box-shadow: ${({ theme }) =>
          `0px 0px 0px 1px inset ${theme.color.primary.brand} !important`};
      }

      &:focus:is(&[data-has-error='true']),
      &:focus-within:is(&[data-has-error='true']) {
        box-shadow: ${({ theme }) =>
          `0px 0px 0px 1px inset ${theme.color.variants.danger.active} !important`};
      }
    }
  }

  ${StyleInputContainer}:first-of-type {
    input {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  ${StyleInputContainer}:nth-child(n + 2):not(${StyleInputContainer}:last-of-type) {
    input {
      border-radius: 0;
      border-left: none;
    }
  }

  ${StyleInputContainer}:last-of-type {
    input {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      border-left: none;
    }
  }
`;

const StyledLinkButton = styled(Button)<{ isConnected: boolean; disabled?: boolean }>`
  align-self: self-start;
  svg {
    rotate: 90deg;
    margin-left: ${Spacing[1]};
    path {
      fill: ${({ disabled, isConnected, theme }) =>
        disabled
          ? theme.colorButtonPrimaryFillDisabled
          : isConnected
            ? theme.color.primary.brand
            : theme.color.primary.black};
      stroke: none !important;
    }
  }
`;

type LinkedStyleInputsProps = {
  defaultValues: StyleValueBoxDimensionsConfig;
  label?: string;
  id: string;
  rootStyle: string;
  submitLinked: (value: string, root: string) => void;
  submitSingle: (value: string, field: string, root: string) => void;
  editable: boolean;
  allowNegativeNumbers?: boolean;
};

const LinkedStyleInputs = ({
  id,
  defaultValues,
  label,
  submitLinked,
  submitSingle,
  editable,
  rootStyle,
  allowNegativeNumbers,
}: LinkedStyleInputsProps) => {
  const [inputsAreLinked, { toggle: toggleLinkedInputs, disable: disableLinkedInputs }] =
    useToggle(false);
  const [linkedValues, setLinkedValues] = useState(defaultValues);

  useEffect(() => {
    setLinkedValues(defaultValues);
  }, [defaultValues]);

  // disable linked inputs whenever the calling component changes
  useEffect(() => {
    disableLinkedInputs();
  }, [id, disableLinkedInputs]);

  const determineLinkedValues = useCallback(
    (value: string) => {
      const newLinkedValues = produce(linkedValues, (draftNewValues) => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        Object.keys(draftNewValues).forEach((key: keyof StyleValueBoxDimensionsConfig) => {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          draftNewValues[key].value = parseInt(value);
        });

        return draftNewValues;
      });
      setLinkedValues(newLinkedValues);
    },
    [linkedValues],
  );

  const submitField = useCallback(
    (value: string, field: string) => {
      if (inputsAreLinked) {
        submitLinked(value, rootStyle);
        determineLinkedValues(value);
      } else {
        submitSingle(value, field, rootStyle);
      }
    },
    [inputsAreLinked, submitSingle, submitLinked, rootStyle, determineLinkedValues],
  );

  return (
    <>
      {label && (
        <StyleLabel size='sm' id={id}>
          {label}
        </StyleLabel>
      )}
      <LinkedInputsContainer>
        <LinkedInputs aria-labelledby={id}>
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          {['top', 'left', 'bottom', 'right'].map((side: keyof StyleValueBoxDimensionsConfig) => (
            <StyleInput
              key={side}
              field={side}
              allowNegativeNumbers={allowNegativeNumbers}
              name={`component-style-${rootStyle}-${side}-${id}`}
              defaultValue={linkedValues[side]?.value.toString() ?? '0'}
              editable={editable}
              hintText={side}
              submit={submitField}
            />
          ))}
        </LinkedInputs>
        <Tooltip
          side='top'
          disabled={!editable}
          trigger={
            <StyledLinkButton
              prefixIcon={<LinkIcon />}
              size='sm'
              width='icon'
              disabled={!editable}
              variant='inverse'
              isConnected={inputsAreLinked}
              onClick={toggleLinkedInputs}
            />
          }
        >
          Click to link inputs
        </Tooltip>
      </LinkedInputsContainer>
    </>
  );
};

export default memo(LinkedStyleInputs);
